import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Modal, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import EntityForm, { EntityErrors, EntityTouched } from "../components/EntityForm"
import { Entity, EntityType } from "../data/models.interface"
import { useGetEntityQuery } from "../hooks/queries/useGetEntityQuery"
import { useGetPassesQuery } from "../hooks/queries/useGetPassesQuery"
import { useGetRolesQuery } from "../hooks/queries/useGetRolesQuery"
import { useSaveEntityQuery } from "../hooks/queries/useSaveEntityQuery"
import { Parameters } from "../hooks/useLazyQuery"
import { useDeleteEntityQuery } from "../hooks/queries/useDeleteEntityQuery"

interface IEntityPage {}
const EntityPage: FunctionComponent<IEntityPage> = () => {
	const params = useParams()
	const entityId = params.entityId
	//
	const entityQuery = useGetEntityQuery(entityId)
	const saveEntityQuery = useSaveEntityQuery(entityId)
	const deleteEntityQuery = useDeleteEntityQuery(entityId)
	const rolesQuery = useGetRolesQuery()
	const passesQuery = useGetPassesQuery()
	//
	const [entity, setEntity] = useState<Entity>()
	const [touched, setTouched] = useState<EntityTouched>({})
	const validationErrors = validate(entity)
	//
	useEffect(() => {
		// only init state once
		if (entity == null) {
			if (entityId === "new") setEntity({ type: EntityType.EMPLOYEE } as Entity)
			else if (entityQuery.data) setEntity(entityQuery.data)
		}
	}, [entityId, entityQuery.data, entity])
	// loading layout
	const loading = !entity || !rolesQuery.data || !passesQuery.data
	const error = saveEntityQuery.error || entityQuery.error || rolesQuery.error || passesQuery.error
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	return (
		<Container>
			<h2 className="text-center mt-4">Employee or Vendor</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<EntityForm
				entity={entity}
				setEntity={setEntity}
				touched={touched}
				setTouched={setTouched}
				validationErrors={validationErrors}
				roles={rolesQuery.data?.roles!}
				passes={passesQuery.data?.passes!}
			/>
			<Row>
				<Col xs="auto">
					<BackButton />
				</Col>
				{/* Hide delete for new entities */}
				{entityId !== "new" && (
					<Col xs="auto">
						<DeleteButton entityId={entityId} query={deleteEntityQuery.query} loading={deleteEntityQuery.loading} />
					</Col>
				)}
				<Col></Col>
				<Col xs="auto">
					<SaveButton
						entity={entity}
						setTouched={setTouched}
						validationErrors={validationErrors}
						query={saveEntityQuery.query}
						loading={saveEntityQuery.loading}
					/>
				</Col>
			</Row>
		</Container>
	)
}
export default EntityPage

interface ILoadingLayout {
	error?: string
}
const LoadingLayout: FunctionComponent<ILoadingLayout> = ({ error }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Employee or Vendor</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

const BackButton = () => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-4" onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}

interface ISaveButton {
	entity: Entity
	setTouched: (value: EntityTouched) => void
	validationErrors: EntityErrors
	query: (entity: Parameters<Entity>) => Promise<void>
	loading: boolean
}
const SaveButton = ({ entity, setTouched, validationErrors, query, loading }: ISaveButton) => {
	const navigate = useNavigate()
	const save = async () => {
		// show errors on all fields
		setTouched({ name: true, type: true })
		//
		try {
			if (Object.values(validationErrors).every((e) => !e)) {
				await query({ body: entity })
				navigate(-1)
			}
		} catch (e) {
			return
		}
	}
	return (
		<Button variant="primary" className="mb-4" disabled={loading} onClick={save}>
			Save
		</Button>
	)
}
const validate = (entity?: Entity) => {
	const validationErrors: EntityErrors = {}
	if (!entity?.name) validationErrors.name = "Name is required."
	if (!entity?.type) validationErrors.type = "Type is required."
	return validationErrors
}

// New DeleteButton component with confirmation modal
interface IDeleteButton {
	entityId?: string
	query: () => Promise<void>
	loading: boolean
}
const DeleteButton = ({ entityId, query, loading }: IDeleteButton) => {
	const [show, setShow] = useState(false)
	const navigate = useNavigate()

	const onConfirm = async () => {
		try {
			await query()
			setShow(false)
			navigate(-1)
		} catch (e) {
			// swallow; hook exposes error for display in page
		}
	}

	return (
		<>
			<Button variant="danger" className="mb-4" disabled={loading} onClick={() => setShow(true)}>
				Delete
			</Button>

			<Modal show={show} onHide={() => setShow(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Confirm Delete</Modal.Title>
				</Modal.Header>
				<Modal.Body>Are you sure you want to delete this entity? This action cannot be undone.</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShow(false)}>
						Cancel
					</Button>
					<Button variant="danger" onClick={onConfirm} disabled={loading}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}
